import {
  cacheKeys,
  FIND_ACCOUNT_ASSIGNMENTS,
  UPDATE_ASSET_ACCOUNTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal } from '../../shared/components/assignment/assignmentModal';
import { EditAccountAssetsList } from './editAccountAssetsList';

type AccountAssetAssignmentFormProps = {
  accountId: string;
  title: string;
  onComplete: () => void;
};

export const AccountAssetAssignmentForm = ({
  accountId,
  title,
  onComplete,
}: AccountAssetAssignmentFormProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();

  const { data, isPending } = useCustomQuery({
    query: FIND_ACCOUNT_ASSIGNMENTS,
    cacheKey: ['account', 'assignments', accountId],
    variables: { accountId, workspaceId: currentWorkspace!.id },
  });
  const assignments: string[] | undefined =
    data?.account?.findAccountAssignments?.assets;

  const { mutateAsync: updateAssetAccounts } = useCustomMutation({
    query: UPDATE_ASSET_ACCOUNTS,
  });

  const handleSave = async ({
    added,
    removed,
  }: {
    added: string[];
    removed: string[];
  }) => {
    const workspaceId = currentWorkspace!.id;
    if (added.length > 0) {
      await updateAssetAccounts({
        input: {
          assetIds: added,
          addedAccountIds: [accountId],
          removedAccountIds: [],
          workspaceId,
        },
      });
    }
    if (removed.length > 0) {
      await updateAssetAccounts({
        input: {
          assetIds: removed,
          addedAccountIds: [],
          removedAccountIds: [accountId],
          workspaceId,
        },
      });
    }
    await queryClient.invalidateQueries({ queryKey: cacheKeys('account').all });
    await queryClient.invalidateQueries({ queryKey: cacheKeys('asset').all });
  };

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending}
      onComplete={onComplete}
      showTabs={false}
      tabs={[
        {
          key: 'assets',
          label: '',
          searchPlaceholder: t('groups.assignments.assetSearchPlaceholder'),
          assignments,
          onSave: handleSave,
          renderList: ({ keyword, maxHeight, assignments, onAssignmentsChange }) => (
            <EditAccountAssetsList
              accountId={accountId}
              workspaceId={currentWorkspace!.id}
              maxHeight={maxHeight}
              keyword={keyword}
              assignments={assignments}
              updateAssignments={onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};
